import { createSignal, Show, onMount } from 'solid-js'
import { useNavigate, useSearchParams } from '@solidjs/router'
import { useAuth } from '../lib/auth'
import { getInvite, acceptInvite, requestOtp, verifyOtp } from '../lib/api'

type Step = 'loading' | 'accept' | 'signup' | 'done'

export default function Invite() {
  const navigate = useNavigate()
  const auth = useAuth()
  const [searchParams] = useSearchParams()

  const [step, setStep] = createSignal<Step>('loading')
  const [invite, setInvite] = createSignal<{ venueName: string; role: string; inviterName: string; existingUser: boolean } | null>(null)
  const [email, setEmail] = createSignal('')
  const [otp, setOtp] = createSignal('')
  const [isProcessing, setIsProcessing] = createSignal(false)
  const [error, setError] = createSignal<string | null>(null)

  // useSearchParams types a param as string | string[] | undefined. Only the first
  // value is meaningful here, and the callers below pass it straight into the API
  // (which needs a plain string) — so narrow it at the boundary.
  const tokenParam = searchParams.token
  const token: string | undefined = Array.isArray(tokenParam) ? tokenParam[0] : tokenParam

  onMount(async () => {
    if (!token) { navigate('/'); return }

    const res = await getInvite(token)
    if (res.data) {
      setInvite(res.data)
      if (res.data.existingUser && auth.isAuthenticated()) {
        setStep('accept')
      } else if (res.data.existingUser) {
        setStep('accept')
      } else {
        setStep('signup')
      }
    } else {
      setError('Invalid or expired invite link.')
    }
  })

  async function handleAccept() {
    // The screen only renders this button after onMount has resolved a token; the
    // guard is for the type system, not for a reachable runtime state.
    if (!token) return
    setIsProcessing(true)
    const res = await acceptInvite(token)
    if (res.data) {
      await auth.refreshProfile()
      setStep('done')
    } else {
      setError(res.error?.message || 'Failed to accept invite.')
    }
    setIsProcessing(false)
  }

  async function handleSendOtp(e: Event) {
    e.preventDefault()
    setIsProcessing(true)
    setError(null)
    const res = await requestOtp(email())
    if (res.error) setError(res.error.message)
    setIsProcessing(false)
  }

  async function handleVerifyOtp(e: Event) {
    e.preventDefault()
    setIsProcessing(true)
    setError(null)
    const res = await verifyOtp(email(), otp())
    if (res.data) {
      await handleAccept()
    } else {
      setError(res.error?.message || 'Verification failed.')
    }
    setIsProcessing(false)
  }

  return (
    <div class="min-h-dvh bg-gray-50 flex flex-col">
      <header class="bg-white border-b border-gray-200 px-4 py-3">
        <div class="max-w-md mx-auto flex items-center gap-2">
          <img src="/logo.svg" alt="ZipCheck" class="h-8" />
          <span class="text-lg font-semibold text-gray-900">ZipCheck</span>
        </div>
      </header>

      <main class="flex-1 flex items-center justify-center p-4">
        <div class="w-full max-w-md">
          {/* Loading */}
          <Show when={step() === 'loading'}>
            <div class="text-center py-12 text-gray-500">Loading invite...</div>
          </Show>

          {/* Error */}
          <Show when={error()}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
              <p class="text-red-600 mb-4">{error()}</p>
              <button onClick={() => navigate('/')}
                class="py-2 px-4 bg-gray-100 text-gray-700 rounded-lg text-sm">Go home</button>
            </div>
          </Show>

          {/* Accept invite (existing user) */}
          <Show when={step() === 'accept' && invite()}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
              <div class="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span class="text-3xl">👋</span>
              </div>
              <h1 class="text-xl font-bold text-gray-900 mb-2">You're invited!</h1>
              <p class="text-sm text-gray-600 mb-6">
                <strong>{invite()!.inviterName}</strong> invited you to join{' '}
                <strong>{invite()!.venueName}</strong> as <strong>{invite()!.role}</strong>.
              </p>

              <Show when={!auth.isAuthenticated()}>
                <p class="text-sm text-gray-500 mb-4">Please sign in to accept.</p>
                <button onClick={() => navigate('/signup')}
                  class="w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700">
                  Sign in
                </button>
              </Show>

              <Show when={auth.isAuthenticated()}>
                <button onClick={handleAccept} disabled={isProcessing()}
                  class="w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                         hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                  {isProcessing() ? 'Accepting...' : 'Accept invite'}
                </button>
              </Show>
            </div>
          </Show>

          {/* Signup + accept (new user) */}
          <Show when={step() === 'signup' && invite()}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h1 class="text-xl font-bold text-gray-900 mb-2">Join {invite()!.venueName}</h1>
              <p class="text-sm text-gray-600 mb-6">
                Create your ZipCheck account to join as <strong>{invite()!.role}</strong>.
              </p>

              <Show when={otp() === ''}>
                <form onSubmit={handleSendOtp}>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" required placeholder="you@example.com" value={email()}
                    onInput={(e) => setEmail(e.currentTarget.value)}
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg text-base
                           focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />

                  <Show when={error()}><p class="mt-2 text-sm text-red-600">{error()}</p></Show>

                  <button type="submit" disabled={isProcessing() || !email()}
                    class="mt-4 w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                           hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                    {isProcessing() ? 'Sending...' : 'Continue'}
                  </button>
                </form>
              </Show>

              <Show when={otp() !== ''}>
                <form onSubmit={handleVerifyOtp}>
                  <p class="text-sm text-gray-600 mb-4">Code sent to <strong>{email()}</strong></p>
                  <input type="text" inputmode="numeric" maxlength={6} required placeholder="000000" value={otp()}
                    onInput={(e) => setOtp(e.currentTarget.value.replace(/\D/g, ''))}
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg text-base text-center
                           text-2xl tracking-[0.5em] font-mono
                           focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />

                  <Show when={error()}><p class="mt-2 text-sm text-red-600">{error()}</p></Show>

                  <button type="submit" disabled={isProcessing() || otp().length !== 6}
                    class="mt-4 w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                           hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                    {isProcessing() ? 'Verifying...' : 'Verify & join'}
                  </button>
                </form>
              </Show>
            </div>
          </Show>

          {/* Done */}
          <Show when={step() === 'done' && invite()}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
              <div class="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span class="text-3xl">✓</span>
              </div>
              <h1 class="text-xl font-bold text-gray-900 mb-2">Welcome to {invite()!.venueName}!</h1>
              <p class="text-sm text-gray-600 mb-6">
                You're now a <strong>{invite()!.role}</strong>. Open the app to start.
              </p>
              <button onClick={() => navigate('/pass')}
                class="w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700">
                Open app
              </button>
            </div>
          </Show>
        </div>
      </main>
    </div>
  )
}
